import { checkAccess } from '@/core/helpers/auth.helper';
import i18n from '@/i18n';
import { checkIsPublicRoute } from '@nxtpeople/nxt-vue-keycloak';
import { isAxiosError } from 'axios';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { publicRoutes } from '../router';
import store from '../store';

const JOURNEY_OVERVIEW_ROUTE = { name: 'journey-overview', query: { page: 1 } };

const AUTH_GUARD = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
   if (checkIsPublicRoute(to, publicRoutes)) return next();

   checkErrorPage(to, next);

   await fetchUserInfo(next);

   const journeyFound = await fetchJourney(to);
   if (!journeyFound) return next(JOURNEY_OVERVIEW_ROUTE);

   if (store.state.userInfo.userInfo) checkIfUserHasPermission(to, from, next);
   else next(from);
};

function checkErrorPage(to: RouteLocationNormalized, next: NavigationGuardNext) {
   const isErrorPage = to.name === 'ErrorPage';
   const hasErrorParams = to.query.error_code && to.query.error_title;

   if (isErrorPage) {
      if (!hasErrorParams) return next('/');
      //user will not be fetched so loading can be disabled
      store.state.userInfo!.loadingUserInfo = false;
      return next();
   }
}

async function fetchUserInfo(next: NavigationGuardNext) {
   if (!store.state.userInfo?.loadingUserInfo) return;
   if (!store.state.userInfo?.userInfo) {
      await store.dispatch('userInfo/getUserInfo').catch(() => {
         store.state.userInfo!.loadingUserInfo = false;

         return next({
            path: '/error',
            query: {
               error_code: '403',
               error_title: i18n.global.t('access-denied'),
               error_message: i18n.global.t('u-have-no-access'),
               error_action: 'home-or-logout',
            },
         });
      });
   }
}

async function fetchJourney(to: RouteLocationNormalized): Promise<boolean> {
   const journey = store.getters.journey;
   if (!to.params.journeyId || (journey && journey.id == to.params.journeyId)) return true;

   try {
      await store.dispatch('getSetJourney', { id: to.params.journeyId });
      return true;
   } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) return false;
      throw error;
   }
}

async function checkIfUserHasPermission(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
   const requiresPermission = !!to.meta.permission || !!to.meta.journey_scopes;
   if (!requiresPermission) return next();

   const requiredScopes = (to.meta.scopes as Array<string>) || ['view'];
   const permission = store.state.authStore?.permissions?.[to.meta.permission as string];

   const requiredScopesForJourney = (to.meta.journey_scopes as Array<string>) || ['view'];
   const journeyPermission = store.getters.journey?.scopes;

   let hasRequiredJourneyPermission;

   if (to.meta.journey_scopes && to.params.journeyId) {
      hasRequiredJourneyPermission = checkAccess(requiredScopesForJourney, journeyPermission);
   } else {
      hasRequiredJourneyPermission = true;
   }

   const hasRequiredPermission = checkAccess(requiredScopes, permission);

   if (hasRequiredPermission && hasRequiredJourneyPermission) return next();

   return next({
      path: '/error',
      query: {
         error_code: '403',
         error_title: i18n.global.t('access-denied'),
         error_message: i18n.global.t('u-have-no-access'),
         error_action: 'home-or-logout',
      },
   });
}

export default AUTH_GUARD;
